import * as React from "react";
import { useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { toast } from "react-toastify";

import {
  ContentFlagReason,
  ContentQuestionRow,
} from "../../common/contentTypes";
import * as Content from "../../services/api/Content";

interface Props {
  questions: ContentQuestionRow[];
  isOpen: boolean;
  onClose: () => void;
  onFlagged: () => void;
}

const REASONS: { value: ContentFlagReason; label: string }[] = [
  { value: "wrong_answer", label: "Wrong answer" },
  { value: "inappropriate", label: "Inappropriate" },
  { value: "too_hard", label: "Too hard" },
  { value: "other", label: "Other" },
];

/** Every path captures a flag: it is the record of why content was questioned. */
export const FlagCaptureModal: React.FC<Props> = ({
  questions,
  isOpen,
  onClose,
  onFlagged,
}) => {
  const [reason, setReason] = useState<ContentFlagReason>("wrong_answer");
  const [comment, setComment] = useState("");
  const [customer, setCustomer] = useState("");
  const [running, setRunning] = useState(false);

  const plural = questions.length === 1 ? "" : "s";

  const submit = async () => {
    setRunning(true);
    const capture = {
      questionIds: questions.map((question) => question.id),
      reason,
      comment: comment.trim() || undefined,
      customer: customer.trim() || undefined,
    };
    try {
      const { removed } = await Content.flagAndRemoveQuestions(capture);
      toast.success(
        `Removed ${removed} question${
          removed === 1 ? "" : "s"
        } and sent to the review queue`,
      );
      setComment("");
      setCustomer("");
      onFlagged();
    } catch (err) {
      toast.error((err as Error).message);
      // Some may have landed before the failure; never leave a stale table.
      onFlagged();
    } finally {
      setRunning(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose}>
      <ModalHeader toggle={onClose}>
        Flag {questions.length} question{plural}
      </ModalHeader>
      <ModalBody>
        {questions.length === 1 ? (
          <p className="text-muted">{questions[0].title}</p>
        ) : (
          <p className="text-muted">
            {questions.length} questions will be flagged together.
          </p>
        )}
        <Form>
          <FormGroup>
            <Label for="content-flag-reason">Reason</Label>
            <Input
              id="content-flag-reason"
              type="select"
              className="custom-select"
              value={reason}
              onChange={(e) => setReason(e.target.value as ContentFlagReason)}
            >
              {REASONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label for="content-flag-comment">Comment (optional)</Label>
            <Input
              id="content-flag-comment"
              type="textarea"
              rows={2}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label for="content-flag-customer">
              Reporting customer (optional)
            </Label>
            <Input
              id="content-flag-customer"
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
            />
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter className="justify-content-between">
        <small className="text-muted">
          Removed now, and sent to the review queue for a second look.
        </small>
        <div>
          <Button color="secondary" onClick={onClose} disabled={running}>
            Cancel
          </Button>
          <Button
            color="danger"
            className="ml-2"
            onClick={submit}
            disabled={running}
          >
            {running ? "Removing…" : "Flag & remove"}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};
